import React, { useState, Suspense, lazy } from 'react';
import { Dropdown } from 'react-bootstrap';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

// Layouts & Shared Components
import AdminLayout from '../../../layouts/AdminLayout';
import PaginationComponent from '../../../shared/components/pagination/PaginationComponent';
import ErrorBoundary from '../../../shared/components/errors/ErrorBoundary';
import SearchComponent from '../../pms-agent/components/SearchComponent';

// Hooks
import useIndexGroups from '../../../services/group/hooks/useIndexGroups';
import { useAuth } from '../../../auth/context/AuthProvider';
import { useDeleteGroup } from '../../../services/group/hooks/useDeleteGroup';

// Types
import { GroupData } from '../../../services/group/response/IndexGroupResponse';

// --- Lazy Loading Dialogs & Modals ---
const CreateGroupDialog = lazy(() => import('../components/CreateGroupDialog'));
// Mengganti EditGroupDialog dengan modal manajemen yang baru
const GroupManagementModal = lazy(() => import('../components/management/GroupManagementModal'));

/**
 * Komponen halaman utama untuk fitur Group.
 * Mengintegrasikan semua fungsionalitas CRUD dan manajemen grup.
 */
const GroupPage: React.FC = () => {
    // State untuk UI halaman
    const [page, setPage] = useState<number>(1);
    const [searchQuery, setSearchQuery] = useState<string>('');

    // State untuk mengontrol visibilitas dialog/modal
    const [showCreateDialog, setShowCreateDialog] = useState<boolean>(false);
    const [editingGroupId, setEditingGroupId] = useState<string | null>(null);

    // Mengambil data grup. 'include' sangat penting untuk menampilkan
    // data relasi (perusahaan, pengguna, dan properti) di UI.
    const { data: groupsData, isSuccess, isError, isLoading } = useIndexGroups({
        search: searchQuery,
        page: page,
        limit: 10,
        orderBy: 'created_at',
        sort: 'desc',
        include: 'company,users,properties',
    });

    const queryClient = useQueryClient();
    const { mutate: deleteGroup, isPending: isDeleting } = useDeleteGroup();
    const { hasRole } = useAuth();

    const onRemove = (group: GroupData) => {
        toast.loading(`Removing group "${group.name}"...`, { id: 'delete-group' });

        deleteGroup({ id: group.id }, {
            onSuccess: async () => {
                toast.success('Group removed successfully', { id: 'delete-group' });
                await queryClient.refetchQueries({ queryKey: ['groups'] });
            },
            onError: (error) => toast.error(error.message || 'Failed to remove group', { id: 'delete-group' }),
        });
    };

    // Handler untuk membuka dan menutup modal manajemen
    const handleOpenManagementModal = (groupId: string) => setEditingGroupId(groupId);
    const handleCloseManagementModal = () => setEditingGroupId(null);
    const handleOpenCreate = () => setShowCreateDialog(true);
    const handleCloseCreate = () => setShowCreateDialog(false);

    return (
        <AdminLayout>
            <ErrorBoundary>
                <div className="container-fluid mt-5">
                    <div className="table-container">
                        <h3 className="mb-3">Groups</h3>
                        <div className="search-container mb-3">
                            <SearchComponent queryKey={searchQuery} onSearch={setSearchQuery} />
                            {hasRole('dev') && (
                                <button className="btn btn-primary" onClick={handleOpenCreate}>
                                    Create
                                </button>
                            )}
                        </div>

                        <div className="table-responsive">
                            <table className="table table-striped">
                                <thead>
                                    <tr>
                                        <th>Group</th>
                                        <th>Count of Properties</th>
                                        <th>Users</th>
                                        <th className='text-end'>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {isLoading && <tr><td colSpan={4} className="text-center">Loading...</td></tr>}
                                    {isError && <tr><td colSpan={4} className="text-center text-danger">Failed to load data.</td></tr>}
                                    {isSuccess && groupsData.data.length === 0 && <tr><td colSpan={4} className="text-center">No groups found.</td></tr>}
                                    {isSuccess && groupsData.data.map((group) => (
                                        <tr key={group.id}>
                                            <td>
                                                <div>{group.name}</div>
                                                <small className="text-muted">{group.company?.name || 'No Company'}</small>
                                            </td>
                                            <td>{group.properties?.length || 0}</td>
                                            <td>
                                                <div className="avatar-list avatar-list-stacked">
                                                    {group.users?.slice(0, 5).map(user => (
                                                        <span key={user.id} className="avatar avatar-sm rounded-circle" title={user.name}>
                                                            {user.name.charAt(0).toUpperCase()}
                                                        </span>
                                                    ))}
                                                    {group.users && group.users.length > 5 && (
                                                        <span className="avatar avatar-sm rounded-circle">
                                                            +{group.users.length - 5}
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="text-end">
                                                <Dropdown>
                                                    <Dropdown.Toggle variant="default" id={`dropdown-group-${group.id}`}>Actions</Dropdown.Toggle>
                                                    <Dropdown.Menu popperConfig={{ strategy: 'fixed' }}>
                                                        <Dropdown.Item onClick={() => handleOpenManagementModal(group.id)}>Edit</Dropdown.Item>
                                                        {hasRole('dev') && (
                                                            <Dropdown.Item onClick={() => onRemove(group)} className='text-danger' disabled={isDeleting}>
                                                                {isDeleting ? 'Removing...' : 'Remove'}
                                                            </Dropdown.Item>
                                                        )}
                                                    </Dropdown.Menu>
                                                </Dropdown>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {isSuccess && groupsData.pagination && groupsData.data.length > 0 && (
                            <PaginationComponent totalPages={groupsData.pagination.last_page} currentPage={groupsData.pagination.current_page} onPageChange={setPage} />
                        )}
                    </div>
                </div>
            </ErrorBoundary>

            <Suspense fallback={<div className="modal-backdrop fade show"></div>}>
                {showCreateDialog && (
                    <CreateGroupDialog show={showCreateDialog} onClose={handleCloseCreate} />
                )}
                {editingGroupId && (
                    <GroupManagementModal
                        groupId={editingGroupId}
                        show={!!editingGroupId}
                        onClose={handleCloseManagementModal}
                    />
                )}
            </Suspense>
        </AdminLayout>
    );
};

export default GroupPage;